import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import BugReportIcon from '@mui/icons-material/BugReport';
import TutorialIcon from '@project/common/components/TutorialIcon';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import TimelineIcon from '@mui/icons-material/Timeline';
import SettingsIcon from '@mui/icons-material/Settings';
import Toolbar from '@mui/material/Toolbar';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip from '@project/common/components/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import GitHubIcon from '@mui/icons-material/GitHub';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import type { LinkProps as MuiLinkProps } from '@mui/material/Link';
import MuiLink from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Popover from '@mui/material/Popover';
import ErrorIcon from '@mui/icons-material/Error';
import type { FileWithId } from '@project/common/file-selector';
import { download } from '@project/common/util';
import { isMobile } from 'react-device-detect';

interface BarProps {
    drawerWidth: number;
    drawerOpen: boolean;
    hidden: boolean;
    controlsOnly?: boolean;
    children?: React.ReactNode;
    title: string;
    subtitleFiles?: FileWithId[];
    lastError?: any;
    onFileSelector?: () => void;
    onDownloadSubtitleFilesAsSrt: () => void;
    onDownloadSubtitleTimeline: () => void;
    onOpenSettings: () => void;
    onOpenCopyHistory: () => void;
    onCopyLastError: (error: string) => void;
}

interface StyleProps {
    drawerWidth: number;
}

const useStyles = makeStyles<Theme, StyleProps, string>((theme) => ({
    title: {
        flexGrow: 1,
    },
    leftButton: {
        marginRight: theme.spacing(1),
    },
    appBar: {
        background: 'linear-gradient(150deg, #343084, #343084 160%)',
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        width: ({ drawerWidth }) => `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: ({ drawerWidth }) => drawerWidth,
    },
    drawerButton: {
        transform: 'scaleX(1)',
        width: 40,
        padding: 8,
        transition: theme.transitions.create(['transform', 'padding', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    drawerButtonShift: {
        transform: 'scaleX(0)',
        width: 0,
        padding: 0,
        transition: theme.transitions.create(['transform', 'padding', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    hide: {
        display: 'none',
    },
    menu: {
        '& .MuiLink-root': {
            textDecoration: 'none',
        },
    },
}));

interface CopyHistoryTooltipStylesProps {
    show: boolean;
}

interface CopyHistoryTooltipProps extends TooltipProps {
    show: boolean;
}

const useCopyHistoryTooltipStyles = makeStyles<Theme, CopyHistoryTooltipStylesProps, string>(() => ({
    tooltip: ({ show }) => ({
        display: show ? 'block' : 'none',
    }),
}));

function CopyHistoryTooltip({ show, ...toolTipProps }: CopyHistoryTooltipProps) {
    const classes = useCopyHistoryTooltipStyles({ show: show });
    return <Tooltip classes={classes} {...toolTipProps} />;
}

const Link = ({ children, ...props }: MuiLinkProps) => {
    return (
        <MuiLink component="a" target="_blank" rel="noreferrer" color="inherit" {...props}>
            {children}
        </MuiLink>
    );
};
export default function Bar({
    drawerWidth,
    drawerOpen,
    hidden,
    controlsOnly = false,
    children,
    title,
    subtitleFiles,
    lastError,
    onOpenSettings,
    onOpenCopyHistory,
    onDownloadSubtitleFilesAsSrt,
    onDownloadSubtitleTimeline,
    onCopyLastError,
}: BarProps) {
    const classes = useStyles({ drawerWidth });
    const canSaveAsSrt =
        subtitleFiles !== undefined && subtitleFiles.find((f) => !f.file.name.endsWith('.sup')) !== undefined;
    const { t } = useTranslation();
    const controlsRef = useRef<HTMLDivElement>(null);
    const downloadRef = useRef<HTMLDivElement>(null);
    const centerRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!controlsOnly) return;
        const controls = controlsRef.current!;
        const toolbar = controls.offsetParent as HTMLElement;
        const updateCenterWidth = () => {
            const bounds = toolbar.getBoundingClientRect();
            const sideWidth = Math.max(
                downloadRef.current!.getBoundingClientRect().right - bounds.left,
                bounds.right - actionsRef.current!.getBoundingClientRect().left
            );
            centerRef.current!.style.maxWidth = `${Math.max(0, bounds.width - sideWidth * 2)}px`;
        };
        const observer = new ResizeObserver(updateCenterWidth);
        observer.observe(controls);
        observer.observe(downloadRef.current!);
        observer.observe(actionsRef.current!);
        updateCenterWidth();
        return () => observer.disconnect();
    }, [controlsOnly]);

    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<HTMLElement>();
    const [downloadMenuOpen, setDownloadMenuOpen] = useState<boolean>(false);
    const handleDownloadMenuOpen = useCallback((e: React.UIEvent) => {
        setDownloadMenuAnchorEl(e.currentTarget as HTMLElement);
        setDownloadMenuOpen(true);
    }, []);
    const handleDownloadMenuClose = useCallback(() => {
        setDownloadMenuOpen(false);
    }, []);
    const handleDownloadOriginalSubtitles = useCallback(() => {
        handleDownloadMenuClose();
        for (const { file } of subtitleFiles ?? []) {
            download(file, file.name);
        }
    }, [handleDownloadMenuClose, subtitleFiles]);
    const handleDownloadSrt = useCallback(() => {
        handleDownloadMenuClose();
        onDownloadSubtitleFilesAsSrt();
    }, [handleDownloadMenuClose, onDownloadSubtitleFilesAsSrt]);
    const handleDownloadTimeline = useCallback(() => {
        handleDownloadMenuClose();
        onDownloadSubtitleTimeline();
    }, [handleDownloadMenuClose, onDownloadSubtitleTimeline]);

    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const handleMenuClose = useCallback(() => {
        setMenuOpen(false);
    }, []);
    const handleMenuOpen = useCallback((e: React.UIEvent) => {
        setMenuAnchorEl(e.currentTarget as HTMLElement);
        setMenuOpen(true);
    }, []);
    const handleCopyLastError = useCallback(async () => {
        if (!lastError) {
            return;
        }

        let errorString: string;

        if (lastError instanceof Error) {
            errorString = `${lastError.message}\n${lastError.stack}`;
        } else {
            errorString = String(lastError);
        }

        await navigator.clipboard.writeText(errorString);
        onCopyLastError(errorString);
    }, [lastError, onCopyLastError]);

    const downloadButton = (
        <>
            {subtitleFiles !== undefined && subtitleFiles.length > 0 && (
                <Tooltip title={t('action.downloadSubtitles')}>
                    <IconButton
                        edge={controlsOnly ? false : 'start'}
                        color="inherit"
                        className={controlsOnly ? undefined : classes.leftButton}
                        onClick={handleDownloadMenuOpen}
                    >
                        <SaveAltIcon />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );
    const actionButtons = (
        <>
            <IconButton edge={controlsOnly ? false : 'end'} color="inherit" onClick={handleMenuOpen}>
                <GitHubIcon />
            </IconButton>
            <Tooltip title={t('bar.settings')}>
                <IconButton edge={controlsOnly ? false : 'end'} color="inherit" onClick={onOpenSettings}>
                    <SettingsIcon />
                </IconButton>
            </Tooltip>
            {!(controlsOnly && isMobile) && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CopyHistoryTooltip title={t('bar.miningHistory')} show={!drawerOpen}>
                        <IconButton
                            edge={controlsOnly ? false : 'end'}
                            color="inherit"
                            className={clsx({
                                [classes.drawerButton]: !controlsOnly,
                                [classes.drawerButtonShift]: drawerOpen,
                            })}
                            onClick={onOpenCopyHistory}
                        >
                            <HistoryIcon />
                        </IconButton>
                    </CopyHistoryTooltip>
                </Box>
            )}
        </>
    );

    return (
        <>
            {controlsOnly ? (
                <Box ref={controlsRef} sx={{ display: 'flex', alignItems: 'center', color: '#fff', width: '100%' }}>
                    <Box ref={downloadRef} sx={{ display: 'flex', flexShrink: 0 }}>
                        {downloadButton}
                    </Box>
                    <Box
                        ref={centerRef}
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'max-content',
                            overflowX: 'auto',
                        }}
                    >
                        {children}
                    </Box>
                    <Box
                        ref={actionsRef}
                        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 'auto' }}
                    >
                        {actionButtons}
                    </Box>
                </Box>
            ) : (
                <AppBar
                    position="static"
                    className={clsx(classes.appBar, {
                        [classes.appBarShift]: drawerOpen,
                        [classes.hide]: hidden,
                    })}
                >
                    <Toolbar>
                        {downloadButton}
                        <Typography variant="h6" noWrap className={classes.title}>
                            {title}
                        </Typography>
                        {actionButtons}
                    </Toolbar>
                </AppBar>
            )}
            <Popover
                open={downloadMenuOpen}
                anchorEl={downloadMenuAnchorEl}
                onClose={handleDownloadMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <List dense onMouseLeave={handleDownloadMenuClose}>
                    <ListItem disablePadding>
                        <ListItemButton onClick={handleDownloadOriginalSubtitles}>
                            <ListItemIcon>
                                <SaveAltIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('action.downloadOriginalSubtitles')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton onClick={handleDownloadSrt} disabled={!canSaveAsSrt}>
                            <ListItemIcon>
                                <SaveAltIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('action.downloadSubtitlesAsSrt')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton onClick={handleDownloadTimeline}>
                            <ListItemIcon>
                                <TimelineIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('action.downloadSubtitleTimelineAsHtml')} />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
            <Popover
                disableEnforceFocus={true}
                open={menuOpen}
                anchorEl={menuAnchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <List className={classes.menu} onMouseLeave={handleMenuClose} dense>
                    <Link href="https://asbplayer-jiten-docs.pages.dev/docs/intro">
                        <ListItem disablePadding>
                            <ListItemButton>
                                <ListItemIcon>
                                    <TutorialIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('action.userGuide')} />
                            </ListItemButton>
                        </ListItem>
                    </Link>
                    <Link href="https://github.com/khajiitvaper2017/asbplayer/issues">
                        <ListItem disablePadding>
                            <ListItemButton>
                                <ListItemIcon>
                                    <BugReportIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('bar.submitIssue')} />
                            </ListItemButton>
                        </ListItem>
                    </Link>
                    {lastError && (
                        <ListItem disablePadding>
                            <ListItemButton onClick={handleCopyLastError}>
                                <ListItemIcon>
                                    <ErrorIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('bar.copyLastError')} />
                            </ListItemButton>
                        </ListItem>
                    )}
                </List>
            </Popover>
        </>
    );
}
